import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------- CSRF double-submit (cookie-auth only) ----------
// The backend issues a non-httpOnly `csrf_token` cookie; we echo its value on
// every state-changing request. Bearer-token callers (pytest etc.) are
// inherently safe and skip this.
const STATE_VERBS = new Set(["post", "put", "patch", "delete"]);

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

let csrfFetchPromise = null;
async function ensureCsrfToken() {
  const existing = readCookie("csrf_token");
  if (existing) return existing;
  if (!csrfFetchPromise) {
    csrfFetchPromise = axios
      .get(`${API}/auth/csrf`, { withCredentials: true })
      .then((r) => r.data?.csrf_token || readCookie("csrf_token"))
      .finally(() => { csrfFetchPromise = null; });
  }
  return csrfFetchPromise;
}

api.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (!STATE_VERBS.has(method)) return config;
  // If the caller already passes an Authorization header, it's a Bearer call.
  const hasBearer = (config.headers?.Authorization || config.headers?.authorization || "")
    .toString()
    .startsWith("Bearer ");
  if (hasBearer) return config;
  const token = await ensureCsrfToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

/* ============================================================
   Currency rounding — accounting practice
   Use commercial round-half-up to 2 decimal places (matches
   Romanian/EU invoicing conventions and how the backend stores
   money). Avoids JS half-to-even surprises on .005 boundaries.
   ============================================================ */
export const round2 = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  // Multiply by 100 with epsilon nudge to defeat 1.005 → 1.00 fp issues,
  // then half-up via Math.round, then divide.
  return Math.round((v + Number.EPSILON) * 100) / 100;
};

const fmt = (n, locale, opts) =>
  round2(n).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...opts,
  });

export const fmtEUR = (n) => `€${fmt(n, "en-EU")}`;
export const fmtRON = (n) => `${fmt(n, "ro-RO")} RON`;

/* ---------- workspace currency (Feb 2026 overhaul) ---------- */
// A single mutable holder set by AuthContext after the tenant loads.
// Pages can also pass an explicit currency to override.
let _workspaceCurrency = "EUR";
export const setWorkspaceCurrency = (c) => {
  const next = (c || "EUR").toUpperCase();
  if (next === "EUR" || next === "USD") _workspaceCurrency = next;
};
export const getWorkspaceCurrency = () => _workspaceCurrency;
export const currencySymbol = (c) =>
  ((c || _workspaceCurrency) === "USD" ? "$" : "€");
/** Format an amount in the active workspace currency. Pass an explicit
 *  `currency` arg to override (rare). */
export const fmtMoney = (n, currency) => {
  const c = (currency || _workspaceCurrency || "EUR").toUpperCase();
  const sym = c === "USD" ? "$" : "€";
  return `${sym}${fmt(n, c === "USD" ? "en-US" : "en-EU")}`;
};
export const fmtNum = (n, d = 2) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return (0).toFixed(d);
  // Round-half-up at the requested precision
  const factor = 10 ** d;
  return (Math.round((v + Number.EPSILON) * factor) / factor).toFixed(d);
};
export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB") : "—");
export const fmtDateTime = (s) =>
  s ? new Date(s).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—";

/* Quantize a number to 2dp for safe persistence (use before sending to API). */
export const qty2 = round2;
